import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from './cell-session';
import { withTokenAuthRecovery } from './cors-auth-recovery';
import { toJunkdrawerServiceUrlParam, resolvePresignedGetUrl } from './functions';

// =============================================================================
// Client base
// =============================================================================
//
// Direct browser-side calls to ``https://{cellId}.api.tranquility.ai``
// ``/junkdrawer-service/v1/*``. This is the only client for the
// junkdrawer-service: the legacy ``/api/protected/junkdrawer/[...path]``
// BFF proxy and its server-side helpers have been removed. All viewers
// (PlainTextViewer, mediaDisplay mbox fetch, pdfViewer via
// ``useJunkdrawerDocumentPage``, and useXlsxData) call into this module
// using the access token resolved by ``useCellServiceSession``.

/** Path prefix for every junkdrawer-service v1 endpoint. */
const API_PREFIX = '/junkdrawer-service/v1';

/** Wall-clock limit for a single junkdrawer GET attempt before we retry once. */
export const JUNKDRAWER_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Error thrown by every direct-CORS call to junkdrawer-service. Carries the
 * upstream HTTP status (when available) and the raw response body for
 * downstream loggers.
 */
export class JunkdrawerApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'JunkdrawerApiError';
    this.status = status;
    this.detail = detail;
  }
}

export class JunkdrawerTimeoutError extends JunkdrawerApiError {
  constructor(message: string) {
    super(message);
    this.name = 'JunkdrawerTimeoutError';
  }
}

/** User-visible copy for junkdrawer failures — never surface upstream ``detail``. */
export const JUNKDRAWER_USER_FACING_ERROR_MESSAGE = 'Error loading file information';

export function junkdrawerUserFacingErrorMessage(_error?: unknown): string {
  return JUNKDRAWER_USER_FACING_ERROR_MESSAGE;
}

/**
 * In-flight GET promises keyed by fully-resolved URL so concurrent callers
 * for the same document page / spreadsheet row window share one network
 * round-trip. The shared work resolves to a ``CachedHttpResponse`` whose
 * body has already been read once — callers must not share a live
 * ``Response`` stream.
 */
const inFlightGets = new Map<string, Promise<CachedHttpResponse>>();

/** Snapshot of a completed GET: body read once, safe for multiple consumers. */
type CachedHttpResponse = {
  ok: boolean;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: ArrayBuffer;
};

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

/** ``true`` when the failure was our per-attempt timeout, not caller cancel. */
export function isJunkdrawerTimeoutError(error: unknown, outerSignal?: AbortSignal): boolean {
  if (outerSignal?.aborted) {
    return false;
  }
  if (error instanceof JunkdrawerTimeoutError) {
    return true;
  }
  return isAbortError(error);
}

type TimedFetchSignal = {
  signal: AbortSignal;
  cleanup: () => void;
  wasTimedOut: () => boolean;
};

/**
 * Merges an optional caller ``AbortSignal`` with a single-attempt timeout.
 * When the caller aborts (unmount / query cancel) the attempt stops without
 * counting as a timeout retry.
 */
function createTimedFetchSignal(
  outerSignal?: AbortSignal,
  timeoutMs: number = JUNKDRAWER_REQUEST_TIMEOUT_MS,
): TimedFetchSignal {
  const controller = new AbortController();
  let timedOut = false;

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onOuterAbort = () => controller.abort();
  if (outerSignal?.aborted) {
    controller.abort();
  } else {
    outerSignal?.addEventListener('abort', onOuterAbort, { once: true });
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      outerSignal?.removeEventListener('abort', onOuterAbort);
    },
    wasTimedOut: () => timedOut && !outerSignal?.aborted,
  };
}

/**
 * Runs ``fn`` at most once per ``cacheKey`` while in flight. Completed or
 * failed requests are evicted so a later mount can fetch again.
 */
function dedupeInFlight(cacheKey: string, fn: () => Promise<CachedHttpResponse>): Promise<CachedHttpResponse> {
  const existing = inFlightGets.get(cacheKey);
  if (existing) {
    return existing;
  }
  const promise = fn().finally(() => {
    inFlightGets.delete(cacheKey);
  });
  inFlightGets.set(cacheKey, promise);
  return promise;
}

function getCachedHeader(cached: CachedHttpResponse, name: string): string | null {
  const lower = name.toLowerCase();
  for (const [key, value] of Object.entries(cached.headers)) {
    if (key.toLowerCase() === lower) {
      return value;
    }
  }
  return null;
}

/**
 * One network GET per URL (deduped, body read once). Caller ``AbortSignal``
 * only affects that caller's promise — it does not cancel the shared fetch
 * other subscribers are awaiting.
 */
async function fetchRawWithTimeoutRetry(
  url: string,
  init: RequestInit,
): Promise<CachedHttpResponse> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    const { signal, cleanup, wasTimedOut } = createTimedFetchSignal();
    try {
      const res = await fetch(url, { ...init, signal });
      const body = await res.arrayBuffer();
      const headers: Record<string, string> = {};
      res.headers.forEach((value, key) => {
        headers[key] = value;
      });
      return {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers,
        body,
      };
    } catch (error) {
      lastError = error;
      if (attempt === 0 && wasTimedOut()) {
        continue;
      }
      if (wasTimedOut()) {
        throw new JunkdrawerTimeoutError(
          `junkdrawer-service request timed out after ${JUNKDRAWER_REQUEST_TIMEOUT_MS}ms`,
        );
      }
      throw error;
    } finally {
      cleanup();
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new JunkdrawerTimeoutError(
        `junkdrawer-service request timed out after ${JUNKDRAWER_REQUEST_TIMEOUT_MS}ms`,
      );
}

async function fetchWithTimeoutRetry(
  url: string,
  init: RequestInit,
  outerSignal?: AbortSignal,
): Promise<CachedHttpResponse> {
  const shared = dedupeInFlight(`GET:${url}`, () => fetchRawWithTimeoutRetry(url, init));

  if (!outerSignal) {
    return shared;
  }

  if (outerSignal.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }

  return new Promise<CachedHttpResponse>((resolve, reject) => {
    const onAbort = () => {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };
    outerSignal.addEventListener('abort', onAbort, { once: true });
    shared.then(
      (result) => {
        outerSignal.removeEventListener('abort', onAbort);
        if (outerSignal.aborted) {
          onAbort();
          return;
        }
        resolve(result);
      },
      (error) => {
        outerSignal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}

/**
 * Build a fully-qualified junkdrawer-service URL for a given cell.
 *
 * ``path`` is the route *after* ``/junkdrawer-service/v1`` (e.g.
 * ``"/spreadsheets/rows"``). A leading slash is optional.
 */
export function buildJunkdrawerUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

/** Query map: scalar values are emitted once; arrays are emitted as repeated keys. */
export type JunkdrawerQuery = CellServiceQuery;

type JunkdrawerSessionArgs = {
  /** ``custom:cell_id`` claim, used to build the per-cell API host. */
  cellId: string;
  /**
   * Real Cognito access token (``token_use: "access"``). In this app that's
   * ``AuthContext.realAccessToken`` / ``features.tokens.rawAccessToken`` —
   * NOT ``AuthContext.accessToken`` (which is the ID token for legacy BFF
   * cookie reasons and would be rejected by the Lambda Authorizer).
   */
  accessToken: string;
};

type JunkdrawerFetchArgs = JunkdrawerSessionArgs & {
  /** Path under ``/junkdrawer-service/v1`` (e.g. ``"/spreadsheets/rows"``). */
  path: string;
  /** Optional query map; arrays produce repeated keys. */
  query?: JunkdrawerQuery;
  signal?: AbortSignal;
};

type JunkdrawerPostArgs = JunkdrawerSessionArgs & {
  path: string;
  body: unknown;
  signal?: AbortSignal;
};

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) {
    throw new JunkdrawerApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new JunkdrawerApiError('Missing access token.');
  }
}

/** Ensure ``?url=`` carries a bare S3 key with literal ``#`` preserved for encoding. */
function withJunkdrawerUrlQuery(query: JunkdrawerQuery | undefined): JunkdrawerQuery | undefined {
  if (!query) return query;
  const rawUrl = query.url;
  if (typeof rawUrl !== 'string' || rawUrl.trim() === '') return query;
  return { ...query, url: toJunkdrawerServiceUrlParam(rawUrl) };
}

/**
 * Generic GET helper for junkdrawer-service that returns parsed JSON typed
 * as ``T`` or throws a ``JunkdrawerApiError``.
 */
export async function junkdrawerFetch<T>(args: JunkdrawerFetchArgs): Promise<T> {
  const { cellId, path, query, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildJunkdrawerUrl(cellId, path), withJunkdrawerUrlQuery(query));

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let cached: CachedHttpResponse;
    try {
      cached = await fetchWithTimeoutRetry(
        url,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
        signal,
      );
    } catch (error) {
      if (isAbortError(error) && signal?.aborted) {
        throw error;
      }
      if (isJunkdrawerTimeoutError(error, signal)) {
        throw error;
      }
      throw new JunkdrawerApiError(
        `Network error calling junkdrawer-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!cached.ok) {
      const detail = new TextDecoder().decode(cached.body);
      throw new JunkdrawerApiError(
        `junkdrawer-service ${path} returned ${cached.status}`,
        cached.status,
        detail || null,
      );
    }

    try {
      const text = new TextDecoder().decode(cached.body);
      return (text ? JSON.parse(text) : null) as T;
    } catch {
      return null as T;
    }
  });
}

/**
 * Generic POST helper for junkdrawer-service that returns parsed JSON typed
 * as ``T`` or throws a ``JunkdrawerApiError``.
 *
 * Wrapped in ``withTokenAuthRecovery`` like every other helper here. This is the
 * upload-presign path, which runs on a user action that can follow a long idle
 * period — exactly when the proactive refresh loop has been throttled and the
 * access token is stale. Without recovery the presign takes the cell
 * authorizer's CORS-opaque 403 as a flat failure and the capture is never
 * uploaded, even though the same call succeeds on the retry a refresh enables.
 */
export async function junkdrawerPost<T>(args: JunkdrawerPostArgs): Promise<T> {
  const { cellId, path, body, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = buildJunkdrawerUrl(cellId, path);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (isAbortError(error) && signal?.aborted) {
        throw error;
      }
      throw new JunkdrawerApiError(
        `Network error calling junkdrawer-service ${path}: ${(error as Error).message}`,
      );
    }

    const detail = await response.text();
    if (!response.ok) {
      throw new JunkdrawerApiError(
        `junkdrawer-service ${path} returned ${response.status}`,
        response.status,
        detail || null,
      );
    }

    try {
      return (detail ? JSON.parse(detail) : null) as T;
    } catch {
      return null as T;
    }
  });
}

/**
 * One blob page fetched from junkdrawer-service for binary endpoints
 * (``/pdf``, ``/ms-word``). The viewer creates an object URL from ``blob``,
 * uses ``contentType`` to pick a renderer, and reads ``pageCount`` to drive
 * pagination UI.
 *
 */
export interface JunkdrawerBlobResponse {
  blob: Blob;
  contentType: string;
  pageCount: number | null;
}

type JunkdrawerBlobFetchArgs = JunkdrawerFetchArgs;

/**
 * Generic GET helper for binary junkdrawer endpoints. Returns a
 * ``JunkdrawerBlobResponse`` so callers can build object URLs and read the
 * page-count header without re-implementing the auth/error plumbing.
 */
export async function junkdrawerBlobFetch(
  args: JunkdrawerBlobFetchArgs,
): Promise<JunkdrawerBlobResponse> {
  const { cellId, path, query, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildJunkdrawerUrl(cellId, path), withJunkdrawerUrlQuery(query));

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let cached: CachedHttpResponse;
    try {
      cached = await fetchWithTimeoutRetry(
        url,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
        signal,
      );
    } catch (error) {
      if (isAbortError(error) && signal?.aborted) {
        throw error;
      }
      if (isJunkdrawerTimeoutError(error, signal)) {
        throw error;
      }
      throw new JunkdrawerApiError(
        `Network error calling junkdrawer-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!cached.ok) {
      const detail = new TextDecoder().decode(cached.body);
      throw new JunkdrawerApiError(
        `junkdrawer-service ${path} returned ${cached.status}`,
        cached.status,
        detail || null,
      );
    }

    const contentType =
      getCachedHeader(cached, 'Content-Type') || 'application/octet-stream';
    const blob = new Blob([cached.body], { type: contentType });
    const rawPageCount = getCachedHeader(cached, 'x-page-count');
    const parsed = rawPageCount ? parseInt(rawPageCount, 10) : NaN;
    const pageCount = Number.isFinite(parsed) && parsed > 0 ? parsed : null;

    return { blob, contentType, pageCount };
  });
}

/**
 * Generic POST-JSON helper for junkdrawer-service. Unlike ``junkdrawerFetch``
 * (GET-only, deduped/cached by URL) this issues a single POST with a JSON body
 * and parses a JSON response typed as ``T``. POST bodies make URL-keyed dedupe
 * meaningless, so this path skips the shared in-flight cache; it still reuses
 * the per-attempt timeout + caller-abort plumbing.
 */
export async function junkdrawerPostJson<T>(args: {
  cellId: string;
  accessToken: string;
  path: string;
  body: unknown;
  signal?: AbortSignal;
}): Promise<T> {
  const { cellId, path, body, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = buildJunkdrawerUrl(cellId, path);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    const timed = createTimedFetchSignal(signal);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
        signal: timed.signal,
      });

      const text = await res.text();

      if (!res.ok) {
        throw new JunkdrawerApiError(
          `junkdrawer-service ${path} returned ${res.status}`,
          res.status,
          text || null,
        );
      }

      try {
        return (text ? JSON.parse(text) : null) as T;
      } catch {
        return null as T;
      }
    } catch (error) {
      if (isAbortError(error) && signal?.aborted) {
        throw error;
      }
      if (timed.wasTimedOut()) {
        throw new JunkdrawerTimeoutError(
          `junkdrawer-service ${path} timed out after ${JUNKDRAWER_REQUEST_TIMEOUT_MS}ms`,
        );
      }
      if (error instanceof JunkdrawerApiError) {
        throw error;
      }
      throw new JunkdrawerApiError(
        `Network error calling junkdrawer-service ${path}: ${(error as Error).message}`,
      );
    } finally {
      timed.cleanup();
    }
  });
}

// =============================================================================
// Health endpoint
// =============================================================================

/**
 * Shape of the ``GET /v1/health`` response. Kept loose because the upstream
 * service may return additional diagnostic fields over time; only ``status``
 * is treated as canonical.
 */
export interface JunkdrawerHealthResponse {
  status?: string;
  [key: string]: unknown;
}

type FetchHealthArgs = {
  cellId: string;
  accessToken: string;
  signal?: AbortSignal;
};

/**
 * Fetch ``GET /v1/health``. Useful as a smoke test for direct-CORS reach
 * to the junkdrawer-service behind the cell API gateway.
 */
export async function fetchJunkdrawerHealth(
  args: FetchHealthArgs,
): Promise<JunkdrawerHealthResponse> {
  const { cellId, accessToken, signal } = args;
  return junkdrawerFetch<JunkdrawerHealthResponse>({
    cellId,
    accessToken,
    path: '/health',
    signal,
  });
}

// =============================================================================
// Spreadsheet endpoints
// =============================================================================

/** A single row returned by ``GET /v1/spreadsheets/rows``. */
export interface JunkdrawerSpreadsheetRow {
  rowIndex: number;
  data: (string | null)[];
}

/** Full response body for ``GET /v1/spreadsheets/rows``. */
export interface JunkdrawerSpreadsheetRowsResponse {
  xlsxUrl: string;
  sheetIndex: number;
  sheetName: string;
  preamble?: string;
  sheetCount: number;
  headers: string[];
  rowCount: number;
  offset: number;
  limit: number;
  rows: JunkdrawerSpreadsheetRow[];
}

type FetchSpreadsheetRowsArgs = {
  cellId: string;
  accessToken: string;
  s3Url: string;
  sheetIndex: number;
  offset: number;
  limit: number;
  signal?: AbortSignal;
};

/** Fetch one page of spreadsheet rows for ``s3Url`` / ``sheetIndex``. */
export async function fetchJunkdrawerSpreadsheetRows(
  args: FetchSpreadsheetRowsArgs,
): Promise<JunkdrawerSpreadsheetRowsResponse> {
  const { cellId, accessToken, s3Url, sheetIndex, offset, limit, signal } = args;
  return junkdrawerFetch<JunkdrawerSpreadsheetRowsResponse>({
    cellId,
    accessToken,
    path: '/spreadsheets/rows',
    query: {
      url: s3Url,
      sheetIndex,
      offset,
      limit,
    },
    signal,
  });
}

// =============================================================================
// Plain-text endpoints
// =============================================================================

/** Full response body for ``GET /v1/plain-text``. */
export interface JunkdrawerPlainTextResponse {
  total_lines: number;
  start: number;
  limit: number;
  lines: string[];
}

type FetchPlainTextArgs = {
  cellId: string;
  accessToken: string;
  s3Url: string;
  start: number;
  limit: number;
  signal?: AbortSignal;
};

/** Fetch one virtual page of plain-text lines for ``s3Url``. */
export async function fetchJunkdrawerPlainTextPage(
  args: FetchPlainTextArgs,
): Promise<JunkdrawerPlainTextResponse> {
  const { cellId, accessToken, s3Url, start, limit, signal } = args;
  return junkdrawerFetch<JunkdrawerPlainTextResponse>({
    cellId,
    accessToken,
    path: '/plain-text',
    query: {
      url: s3Url,
      start,
      limit,
    },
    signal,
  });
}

// =============================================================================
// Mbox endpoints
// =============================================================================

type FetchMboxEmailArgs = {
  cellId: string;
  accessToken: string;
  s3Url: string;
  emailIndex: number;
  signal?: AbortSignal;
};

/**
 * Fetch a single email from an mbox archive.
 *
 * Returns the raw JSON shape from junkdrawer-service; consumers (currently
 * ``mediaDisplay.fetchMboxData``) own the parsing.
 */
export async function fetchJunkdrawerMboxEmail<T = unknown>(
  args: FetchMboxEmailArgs,
): Promise<T> {
  const { cellId, accessToken, s3Url, emailIndex, signal } = args;
  return junkdrawerFetch<T>({
    cellId,
    accessToken,
    path: '/mbox',
    query: {
      url: s3Url,
      emailIndex,
    },
    signal,
  });
}

// =============================================================================
// Email endpoints (Outlook-style read-only viewer)
// =============================================================================
//
// Sibling to the legacy ``/mbox`` endpoint, but strictly DB-backed: the
// loader populates ``email`` / ``attachment`` rows in the tenant's Postgres
// schema and these handlers serve them without re-parsing the mbox/pst/ost
// file. Used by ``/email-viewer``.

/** One row in the inbox sidebar — slim shape, no body, no attachments. */
export interface JunkdrawerEmailSummary {
  emailIndex: number;
  subject: string | null;
  fromAddress: string | null;
  toAddresses: string[];
  /** ISO 8601, parsed from the RFC 822 header. ``null`` when unparseable. */
  date: string | null;
  /** Raw RFC 822 ``Date:`` header value. */
  dateRaw: string | null;
  attachmentCount: number;
  /** Whitespace-collapsed prefix of the body, ellipsis-truncated. */
  bodySnippet: string | null;
}

/** Page of ``JunkdrawerEmailSummary`` rows + pagination metadata. */
export interface JunkdrawerEmailListResponse {
  mboxUrl: string;
  mediaId: number;
  total: number;
  offset: number;
  limit: number;
  sortDesc: boolean;
  dateFrom: string | null;
  dateTo: string | null;
  emails: JunkdrawerEmailSummary[];
}

/** One attachment row attached to a ``JunkdrawerEmailDetail``. */
export interface JunkdrawerEmailAttachment {
  attachmentIndex: number;
  /** S3 URL of the attachment object (not fetched directly by the SPA in v1). */
  attachmentUrl: string;
  fileName: string;
  contentType: string | null;
  contentLength: number;
}

/** Full email shape for the reader pane (subject + headers + body + attachments). */
export interface JunkdrawerEmailDetail {
  emailIndex: number;
  mboxUrl: string;
  emailUrl: string;
  attachmentUrl?: string | null;
  subject: string | null;
  fromAddress: string | null;
  toAddresses: string[];
  ccAddresses: string[];
  bccAddresses: string[];
  date: string | null;
  dateRaw: string | null;
  body: string | null;
  attachmentCount: number;
  attachments: JunkdrawerEmailAttachment[];
}

type FetchEmailListArgs = {
  cellId: string;
  accessToken: string;
  s3Url: string;
  offset: number;
  limit: number;
  sortDesc: boolean;
  /** ISO 8601 string. Omit / pass ``undefined`` to skip the filter. */
  dateFrom?: string;
  /** ISO 8601 string. Omit / pass ``undefined`` to skip the filter. */
  dateTo?: string;
  signal?: AbortSignal;
};

/**
 * Fetch one page of the Outlook-style inbox for ``s3Url``.
 *
 * Returns 404 with a "Has the loader ingested it yet?" detail when the
 * mailbox hasn't been loaded; callers should surface that as a friendly
 * empty state rather than a hard error.
 */
export async function fetchJunkdrawerEmailList(
  args: FetchEmailListArgs,
): Promise<JunkdrawerEmailListResponse> {
  const { cellId, accessToken, s3Url, offset, limit, sortDesc, dateFrom, dateTo, signal } = args;
  return junkdrawerFetch<JunkdrawerEmailListResponse>({
    cellId,
    accessToken,
    path: '/emails',
    query: {
      url: s3Url,
      offset,
      limit,
      sortDesc,
      dateFrom,
      dateTo,
    },
    signal,
  });
}

type FetchEmailDetailArgs = {
  cellId: string;
  accessToken: string;
  s3Url: string;
  emailIndex: number;
  signal?: AbortSignal;
};

/** Fetch the full reader-pane payload for a single email by index. */
export async function fetchJunkdrawerEmailDetail(
  args: FetchEmailDetailArgs,
): Promise<JunkdrawerEmailDetail> {
  const { cellId, accessToken, s3Url, emailIndex, signal } = args;
  return junkdrawerFetch<JunkdrawerEmailDetail>({
    cellId,
    accessToken,
    path: '/emails/detail',
    query: {
      url: s3Url,
      emailIndex,
    },
    signal,
  });
}

/**
 * One hit from ``POST /v1/emails/search``: the full DB-backed email
 * (``JunkdrawerEmailDetail`` shape) plus ``matchedText`` — the best-ranked
 * knowledge-base chunk that caused the email to surface for the query.
 *
 * Note the search endpoint returns the *full* email (body + attachments), not
 * the slim ``JunkdrawerEmailSummary`` the sidebar list returns; there is no
 * ``bodySnippet`` field — use ``matchedText`` for the preview line instead.
 */
export interface JunkdrawerEmailSearchResult extends JunkdrawerEmailDetail {
  matchedText: string | null;
}

/** Response body for ``POST /v1/emails/search``. */
export interface JunkdrawerEmailSearchResponse {
  query: string;
  total: number;
  hits: JunkdrawerEmailSearchResult[];
}

type SearchEmailsArgs = {
  cellId: string;
  accessToken: string;
  /**
   * Mbox S3 URL the viewer is passed (sent as ``mboxUrl``). Scopes the search
   * to this mailbox whether the mbox is a source file itself or a child of one.
   */
  s3Url: string;
  /**
   * Provenance ``fileUuid`` for the mbox URL (sent as ``sourceUrlUuid``),
   * available from file-info for the mbox URL. The search endpoint uses it
   * alongside ``mboxUrl``; resolved on load. Omitted when not available.
   */
  sourceUrlUuid?: string | null;
  /** Free-text query for the hybrid BM25 + vector search. */
  query: string;
  /** Max deduped emails to return (server clamps to 1..200). */
  topK?: number;
  signal?: AbortSignal;
};

/**
 * Hybrid (BM25 + vector kNN) search across a single mailbox's emails.
 *
 * Relevance-ranked, deduped to one result per email, capped at ``topK``. The
 * backend ignores pagination / date-range / sort — those are list-only
 * concerns. Returns 409 when the tenant has no Turbopuffer namespace and 404
 * semantics are not expected here (an un-ingested mailbox simply yields no
 * hits).
 */
export async function searchJunkdrawerEmails(
  args: SearchEmailsArgs,
): Promise<JunkdrawerEmailSearchResponse> {
  const { cellId, accessToken, s3Url, sourceUrlUuid, query, topK = 50, signal } = args;
  return junkdrawerPostJson<JunkdrawerEmailSearchResponse>({
    cellId,
    accessToken,
    path: '/emails/search',
    body: {
      query,
      // Always the mbox URL the viewer is passed. Scopes the search to this
      // mailbox whether the mbox is a source file itself or a child of one.
      mboxUrl: s3Url,
      ...(sourceUrlUuid ? { sourceUrlUuid } : {}),
      topK,
    },
    signal,
  });
}

// =============================================================================
// Presigned-URL endpoint
// =============================================================================

/**
 * Response from ``GET /v1/pre-sign``: a 4-hour S3 presigned GET URL plus the
 * UTC instant it expires. The frontend drops ``presignedUrl`` straight into
 * an HTML5 ``<audio>``/``<video>`` ``src`` so byte streaming + Range happen
 * directly between the browser and S3, bypassing the cell API gateway for
 * media bytes.
 */
export interface JunkdrawerPresignedUrlResponse {
  presignedUrl: string;
  expiration: string;
}

type FetchPresignedUrlArgs = {
  cellId: string;
  accessToken: string;
  /** Source S3 URL or bare key (s3://, bucket/key, data/bulk/..., files/...). */
  s3Url: string;
  signal?: AbortSignal;
};

/**
 * Mint a 4-hour S3 presigned GET URL for ``s3Url`` via junkdrawer-service.
 * Authenticated with ``Authorization: Bearer {accessToken}`` against the cell
 * API gateway; the returned URL is self-authenticating and safe to use as the
 * ``src`` of a media element.
 */
export async function fetchJunkdrawerPresignedUrl(
  args: FetchPresignedUrlArgs,
): Promise<JunkdrawerPresignedUrlResponse> {
  const { cellId, accessToken, s3Url, signal } = args;
  const res = await junkdrawerFetch<JunkdrawerPresignedUrlResponse>({
    cellId,
    accessToken,
    path: '/pre-sign',
    query: { url: s3Url },
    signal,
  });
  return {
    ...res,
    presignedUrl: resolvePresignedGetUrl(res.presignedUrl),
  };
}

export type JunkdrawerUploadPresignRequest = {
  mimeType: 'image/webp' | 'image/png';
  purpose:
    | 'capture-favorite'
    | 'csam-capture'
    | 'event-media-capture'
    | 'video-still-frame'
    | 'pdf-page-thumbnail';
  caseId: string;
  /** MD5 of the original file from which the user-generated image was captured. */
  originMd5: string;
  /** Exact byte size of the PUT body; signed into the presigned URL. */
  contentLength: number;
  tenantFriendlyName?: string;
  /**
   * Original object URL/key. Required for origin-MD5 binding; for
   * ``csam-capture`` it also identifies the parent whose moderation tags are
   * inherited.
   */
  sourceKey: string;
};

export interface JunkdrawerUploadPresignResponse extends JunkdrawerPresignedUrlResponse {
  s3Uri: string;
  /** Exact value for the required x-amz-tagging header on the presigned PUT. */
  objectTagging: string;
}

type FetchUploadPresignArgs = JunkdrawerSessionArgs & {
  body: JunkdrawerUploadPresignRequest;
  signal?: AbortSignal;
};

/**
 * Mint a short-lived S3 presigned PUT URL for a user-generated image under
 * ``user_generated_files/<caseId>/<userId>/<originMd5>/`` in the tenant
 * processed bucket.
 */
export async function fetchJunkdrawerUploadPresign(
  args: FetchUploadPresignArgs,
): Promise<JunkdrawerUploadPresignResponse> {
  const { cellId, accessToken, body, signal } = args;
  return junkdrawerPost<JunkdrawerUploadPresignResponse>({
    cellId,
    accessToken,
    path: '/pre-sign/upload',
    body,
    signal,
  });
}

// =============================================================================
// Document (PDF / DOCX → PDF) endpoints
// =============================================================================

/**
 * Document API path: ``"pdf"`` for native PDFs, ``"ms-word"`` for DOCX,
 * ``"ms-powerpoint"`` for PPT/PPTX. The non-PDF paths trigger server-side
 * conversion to PDF before per-page rendering.
 */
export type JunkdrawerDocumentApiPath = 'pdf' | 'ms-word' | 'ms-powerpoint';

type FetchDocumentPageBlobArgs = {
  cellId: string;
  accessToken: string;
  /** Source S3 URL (passed through as the upstream ``url`` query param). */
  s3Url: string;
  /** 1-based page number. */
  page: number;
  /** Document family. ``"ms-word"`` triggers server-side DOCX→PDF rendering. */
  apiPath: JunkdrawerDocumentApiPath;
  signal?: AbortSignal;
};

/**
 * Fetch one rendered document page as a blob, plus the upstream
 * ``x-page-count`` header (when CORS-exposed) so the viewer can drive
 * pagination without the legacy ``PdfPageCount`` cookie.
 */
export async function fetchJunkdrawerDocumentPageBlob(
  args: FetchDocumentPageBlobArgs,
): Promise<JunkdrawerBlobResponse> {
  const { cellId, accessToken, s3Url, page, apiPath, signal } = args;
  return junkdrawerBlobFetch({
    cellId,
    accessToken,
    path: `/${apiPath}`,
    query: {
      url: s3Url,
      page,
      format: 'pdf',
    },
    signal,
  });
}

// =============================================================================
// Query keys
// =============================================================================

/**
 * Hierarchical TanStack Query key factory for every junkdrawer-service
 * resource. Use the most specific key in each hook so partial invalidation
 * (``queryClient.invalidateQueries({ queryKey: junkdrawerKeys.spreadsheets() })``)
 * is straightforward.
 */
export const junkdrawerKeys = {
  all: ['junkdrawer'] as const,

  health: (cellId: string | null) => [...junkdrawerKeys.all, 'health', cellId] as const,

  spreadsheets: () => [...junkdrawerKeys.all, 'spreadsheets'] as const,
  spreadsheetRows: (cellId: string | null, s3Url: string, sheetIndex: number) =>
    [...junkdrawerKeys.spreadsheets(), 'rows', cellId, s3Url, sheetIndex] as const,

  plainText: () => [...junkdrawerKeys.all, 'plainText'] as const,
  plainTextPage: (cellId: string | null, s3Url: string, start: number, limit: number) =>
    [...junkdrawerKeys.plainText(), cellId, s3Url, { start, limit }] as const,

  mbox: () => [...junkdrawerKeys.all, 'mbox'] as const,
  mboxEmail: (cellId: string | null, s3Url: string, emailIndex: number) =>
    [...junkdrawerKeys.mbox(), cellId, s3Url, emailIndex] as const,

  emails: () => [...junkdrawerKeys.all, 'emails'] as const,
  emailList: (
    cellId: string | null,
    s3Url: string,
    params: {
      offset: number;
      limit: number;
      sortDesc: boolean;
      dateFrom?: string;
      dateTo?: string;
    },
  ) => [...junkdrawerKeys.emails(), 'list', cellId, s3Url, params] as const,
  emailDetail: (cellId: string | null, s3Url: string, emailIndex: number) =>
    [...junkdrawerKeys.emails(), 'detail', cellId, s3Url, emailIndex] as const,
  emailSearch: (
    cellId: string | null,
    s3Url: string,
    params: { query: string; topK: number; sourceUrlUuid?: string | null },
  ) => [...junkdrawerKeys.emails(), 'search', cellId, s3Url, params] as const,

  documents: () => [...junkdrawerKeys.all, 'documents'] as const,
  documentPage: (
    cellId: string | null,
    s3Url: string,
    apiPath: JunkdrawerDocumentApiPath,
    page: number,
  ) => [...junkdrawerKeys.documents(), apiPath, cellId, s3Url, page] as const,

  presign: () => [...junkdrawerKeys.all, 'presign'] as const,
  presignUrl: (cellId: string | null, s3Url: string) =>
    [...junkdrawerKeys.presign(), cellId, s3Url] as const,
};
